import { useCallback, useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { noteVisit } from '../onboarding.ts';
import { Link, NavLink, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useSession } from '../session.ts';
import { presetRange, useFilters } from '../filters.tsx';
import { isCurrent, navFor, type NavItem } from '../nav.ts';
import BrandMark from './BrandMark.tsx';
import { Icon } from './Icons.tsx';
import { JournalStatus, useJournalStatus } from './JournalStatus.tsx';
import { TickerSearch } from './TickerSearch.tsx';

// Where each page (path + query) was scrolled to, so coming back to it returns there. In memory only: a
// reload starts fresh.
const scrollMemory = new Map<string, number>();

/** Scroll to `y` once the page is tall enough (its data may still be loading), giving up after a moment. */
function restoreScroll(y: number) {
  const until = Date.now() + 1500;
  const tick = () => {
    const room = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo(0, Math.min(y, room));
    if (room < y && Date.now() < until) setTimeout(tick, 50);
  };
  tick();
}

// Desktop: a sidebar that collapses to a rail of icons with small labels. Phones: a top bar, four tabs
// plus "More" at the bottom.

type SidebarMode = 'expanded' | 'rail';
const SIDEBAR_KEY = 'tj_sidebar';
const WIDE = '(min-width: 1440px)';

/** Expanded on wide screens and a rail on laptops, unless the person picked one on this device. */
function useSidebarMode(): [SidebarMode, () => void] {
  const [picked, setPicked] = useState<SidebarMode | null>(() => {
    try { const v = localStorage.getItem(SIDEBAR_KEY); return v === 'expanded' || v === 'rail' ? v : null; } catch { return null; }
  });
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);
  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const mode = picked ?? (wide ? 'expanded' : 'rail');
  const toggle = useCallback(() => {
    const next = mode === 'rail' ? 'expanded' : 'rail';
    setPicked(next);
    try { localStorage.setItem(SIDEBAR_KEY, next); } catch { /* ignore */ }
  }, [mode]);
  // "[" toggles it, like Linear, unless the person is typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);
  return [mode, toggle];
}

export default function Layout({ children }: { children: ReactNode }) {
  const { me, signOut } = useSession();
  const journal = useJournalStatus();
  const toJournal = journal ? journal.total - journal.done : 0;
  const location = useLocation();
  const { pathname } = location;
  const navType = useNavigationType();
  const pageKey = pathname + location.search;
  const [mode, toggleSidebar] = useSidebarMode();
  const rail = mode === 'rail';
  useEffect(() => { noteVisit(pathname); }, [pathname]); // for the getting-started guide
  // A new page opens at the top (carrying a long page's scroll onto a short one also left iOS home-screen
  // apps drawing the tab bar too high). Coming back to a page, with the browser's or phone's back or a
  // "Back to …" link (state.restoreScroll), returns to where it was scrolled.
  useEffect(() => { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; }, []);
  useEffect(() => {
    const back = navType === 'POP' || !!(location.state as { restoreScroll?: boolean } | null)?.restoreScroll;
    const y = back ? scrollMemory.get(pageKey) ?? 0 : 0;
    if (y > 0) restoreScroll(y); else window.scrollTo(0, 0);
    // Saved as you scroll, and again on every tap, click or key (so the spot you leave from is always the
    // last one saved). Only while the address bar still shows this page: right after leaving, the next
    // page's short loading state makes the browser clamp the scroll to 0, which isn't this page's position.
    const remember = () => {
      if (window.location.pathname + window.location.search === pageKey) scrollMemory.set(pageKey, window.scrollY);
    };
    const events = ['scroll', 'pointerdown', 'keydown'] as const;
    events.forEach((e) => window.addEventListener(e, remember, { passive: true, capture: true }));
    return () => events.forEach((e) => window.removeEventListener(e, remember, { capture: true }));
  }, [location.key]);
  // Turning a phone swaps the sidebar layout (landscape) for the top-bar one (portrait), and iOS keeps roughly
  // the old scroll offset, which tucked the page title under the top bar. If you were at the top, stay there.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)');
    let atTop = window.scrollY < 80;
    let settleUntil = 0; // the browser's own scroll adjustments while the screen turns don't count
    let pinUntil = 0; // after the layout switches, hold the top while iOS finishes turning
    const onScroll = () => {
      if (Date.now() < pinUntil) { if (window.scrollY !== 0) window.scrollTo(0, 0); return; }
      if (Date.now() > settleUntil) atTop = window.scrollY < 80;
    };
    const onResize = () => { settleUntil = Date.now() + 600; };
    const onChange = () => {
      if (!atTop) return;
      pinUntil = Date.now() + 1000;
      requestAnimationFrame(() => window.scrollTo(0, 0));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    mq.addEventListener('change', onChange);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      mq.removeEventListener('change', onChange);
    };
  }, []);

  const nav = navFor(me.isAdmin);
  const main = nav.filter((n) => n.place === 'main');
  const foot = nav.filter((n) => n.place === 'foot');
  const tabs = nav.filter((n) => n.tab);
  // The More tab lights up for everything that isn't a tab of its own, Research included.
  const inMore = pathname === '/more' || pathname.startsWith('/research') || nav.some((n) => !n.tab && isCurrent(n, pathname));
  const badge = (n: NavItem) => (n.badge && toJournal > 0 ? toJournal : 0);
  // Badges show up to 99, then "99+" (the full count is in the label), so they never outgrow their icon.
  const shown = (n: number) => (n > 99 ? '99+' : String(n));
  const badgeLabel = (n: number) => `${n} trade${n === 1 ? '' : 's'} to journal`;
  // The badge itself opens what it counts: Journal's "Needs notes" over the last 30 days, every other filter
  // cleared, so the number on the page is the number you tapped. The rest of the link opens Journal as it was.
  const { setFilters } = useFilters();
  const navigate = useNavigate();
  const openBadge = (e: MouseEvent) => {
    if (!(e.target as HTMLElement).closest('.nav-badge, .tab-badge')) return;
    e.preventDefault();
    setFilters({ preset: '30d', ...presetRange('30d'), symbol: '', tag: '', side: '', result: '', setup: '', mistake: '', account: '', needs: '1' });
    navigate('/journal');
  };

  const link = (n: NavItem, extra = '') => {
    const I = n.icon;
    const count = badge(n);
    return (
      <NavLink key={n.to} to={n.to} end={n.to === '/'} title={rail ? n.label : undefined} onClick={count > 0 ? openBadge : undefined}
        className={({ isActive }) => `nav-link ${extra}${isActive ? ' active' : ''}`}>
        <span className="nav-icon"><I />{rail && count > 0 && <span className="nav-badge dot" aria-label={badgeLabel(count)} title={badgeLabel(count)}>{shown(count)}</span>}</span>
        <span className="nav-label">{n.label}</span>
        {!rail && count > 0 && <span className="nav-badge" aria-label={badgeLabel(count)} title={badgeLabel(count)}>{shown(count)}</span>}
      </NavLink>
    );
  };

  return (
    <div className={`app ${rail ? 'with-rail' : ''}`}>
      <aside className={`sidebar ${rail ? 'rail' : ''}`}>
        <div className="sidebar-top">
          <NavLink to="/" className="brand" aria-label="Edgebook home"><BrandMark /><span className="wordmark">Edgebook</span></NavLink>
          <button type="button" className="sidebar-toggle" onClick={toggleSidebar}
            aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'} title={`${rail ? 'Expand' : 'Collapse'} sidebar ( [ )`}>
            <Icon.sidebar />
          </button>
        </div>
        <nav className="nav-main" aria-label="Main">{main.map((n) => link(n))}</nav>
        {/* Journaling is daily work, so its progress sits with the main pages, not with the account links. */}
        <JournalStatus status={journal} variant={rail ? 'rail' : 'sidebar'} />
        <div className="sidebar-foot">
          {foot.map((n) => link(n, 'quiet'))}
          <div className="sign-out-group">
            {!rail && <div className="muted small sidebar-email" title={me.email}>{me.email}</div>}
            <button type="button" className="nav-link quiet sign-out" onClick={signOut} title={rail ? `Sign out ${me.email}` : undefined}>
              <span className="nav-icon"><Icon.signOut /></span><span className="nav-label">Sign out</span>
            </button>
          </div>
        </div>
      </aside>
      <header className="mobile-top">
        <NavLink to="/" className="brand" aria-label="Edgebook home"><BrandMark size={30} /><span className="wordmark">Edgebook</span></NavLink>
        <span className="spacer" />
        <Link to="/research" className="icon-btn" aria-label="Search tickers"><Icon.search /></Link>
      </header>
      <main className="main">
        <div className="main-head hide-mobile"><TickerSearch hotkey autoFocus={pathname === '/research'} /></div>
        {me.recoveryCodesLeft === 0 && pathname !== '/settings' && (
          <div className="banner warn" style={{ marginBottom: 16 }}>
            You have no password recovery codes. <Link to="/settings">Generate them in Settings</Link> so you can reset your password if you forget it.
          </div>
        )}
        {children}
      </main>
      <nav className="bottom-nav" aria-label="Main">
        {tabs.map(({ to, label, icon: I, ...n }) => (
          <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => (isActive ? 'active' : '')} onClick={n.badge && toJournal > 0 ? openBadge : undefined}>
            <I /> {label}
            {n.badge && toJournal > 0 && <span className="tab-badge" aria-label={badgeLabel(toJournal)}>{shown(toJournal)}</span>}
          </NavLink>
        ))}
        <NavLink to="/more" className={inMore ? 'active' : ''}>
          <Icon.more /> More
        </NavLink>
      </nav>
    </div>
  );
}
